/**
 * Pure reactive-effect runner: given the fields that just changed, fire the
 * `reset` / `autoFill` rules whose triggers include a changed field and whose
 * `when` (if any) passes. Value mutations are emitted through the injected
 * `apply(target, value)` callback (which the store routes to the owning form or
 * persists for unmounted targets). Reads `{ ...values, ...changed }` so the
 * just-changed field is fresh.
 */
import type { FieldRule, QualifiedId, ResetTo, ValueSource } from '../../config/fieldRulesConfig';
import type { ComputeHelpers, OptionResolvers, RuleContext, RuleValues } from '../types';
import { conditionSources, evaluateCondition } from './evaluateCondition';

const isEmpty = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

const targetsOf = (rule: FieldRule): QualifiedId[] => (Array.isArray(rule.target) ? rule.target : [rule.target]);

const triggersOf = (rule: FieldRule): QualifiedId[] =>
    rule.triggers && rule.triggers.length > 0 ? rule.triggers : conditionSources(rule.when);

function resetValue(to: ResetTo | undefined): unknown {
    switch (to) {
        case 'emptyArray':
            return [];
        case 'null':
            return null;
        case 'undefined':
        default:
            return undefined;
    }
}

export function resolveAutoFillValue(
    source: ValueSource,
    values: RuleValues,
    ctx: RuleContext,
    helpers: ComputeHelpers = {},
    resolvers: OptionResolvers = {},
): unknown {
    switch (source.from) {
        case 'field':
            return values[source.field];
        case 'static':
            return source.value;
        case 'compute':
            return helpers[source.fn]?.(values, ctx);
        case 'option': {
            const raw = values[source.field];
            if (source.pick === 'value') return raw;
            const options = resolvers.baseOptions?.(source.field) ?? [];
            return options.find((opt) => opt.value === raw)?.label ?? raw;
        }
        default:
            return undefined;
    }
}

export function applyFieldReactions(
    rules: FieldRule[],
    changed: RuleValues,
    values: RuleValues,
    ctx: RuleContext,
    apply: (target: QualifiedId, value: unknown) => void,
    helpers: ComputeHelpers = {},
    resolvers: OptionResolvers = {},
): void {
    const changedKeys = Object.keys(changed);
    if (changedKeys.length === 0) return;

    const changedSet = new Set(changedKeys);
    const merged: RuleValues = { ...values, ...changed };

    for (const rule of rules) {
        const effect = rule.effect;
        if (effect.kind !== 'reset' && effect.kind !== 'autoFill') continue;

        if (!triggersOf(rule).some((trigger) => changedSet.has(trigger))) continue;
        if (!evaluateCondition(rule.when, merged, ctx)) continue;

        const targets = targetsOf(rule);
        if (effect.kind === 'reset') {
            const value = resetValue(effect.to);
            targets.forEach((target) => apply(target, value));
        } else {
            const value = resolveAutoFillValue(effect.value, merged, ctx, helpers, resolvers);
            targets.forEach((target) => {
                if (!effect.overwrite && !isEmpty(merged[target])) return;
                apply(target, value);
            });
        }
    }
}
